import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { cartApi, catalogApi, type Product } from '@/lib/api';
import { PriceDisplay } from '@/components/PriceDisplay';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

export function Cart() {
  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: async () => (await cartApi.get()).data,
  });
  const items = cart.data?.items ?? [];
  const productsQ = useQuery({
    queryKey: ['cart-products', items.map((i) => i.productId).join(',')],
    queryFn: async () => {
      const arr = await Promise.all(items.map((i) => catalogApi.get(i.productId).then((r) => r.data).catch(() => null)));
      const map: Record<string, Product> = {};
      arr.forEach((p) => { if (p) map[p.id] = p; });
      return map;
    },
    enabled: items.length > 0,
  });

  if (cart.isLoading) return <Skeleton className="h-32" />;

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <h1 className="font-display text-3xl mb-3">Your cart is empty</h1>
        <p className="text-(--color-ink-muted) mb-6">Owls are circling, waiting to deliver.</p>
        <Button asChild variant="cta"><Link to="/">Back to the Alley</Link></Button>
      </div>
    );
  }

  const total = items.reduce((sum, it) => {
    const p = productsQ.data?.[it.productId];
    return sum + (p ? p.price_knuts * it.quantity : 0);
  }, 0);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="font-display text-3xl">Your Cart</h1>
      <ul className="divide-y rounded-lg border bg-card">
        {items.map((it) => {
          const p = productsQ.data?.[it.productId];
          return (
            <li key={it.productId} className="p-4 flex items-center gap-4">
              <div className="flex-1">
                <p className="font-medium">{p?.name ?? it.productId}</p>
                <p className="text-sm text-(--color-ink-muted)">×{it.quantity}</p>
              </div>
              <PriceDisplay knuts={(p?.price_knuts ?? 0) * it.quantity} />
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between border-t pt-4">
        <span className="font-display text-xl">Total</span>
        <PriceDisplay knuts={total} className="text-2xl" />
      </div>
      <Button asChild variant="cta" size="lg" className="w-full">
        <Link to="/checkout">Proceed to Checkout</Link>
      </Button>
    </div>
  );
}
